import type { BookingBlockReason } from '@/services/order';
import type { SwitchDirection } from '@/helpers/plan-switch';
import type { WaitlistEntryStatus } from '@/services/waitlist';
import type { WaitlistMode } from '@/state/tenant';
import type { InstallSource } from '@/lib/home-screen-app-store';
import type { InstallPlatform } from '@/lib/install-platform';

import { posthog, isPostHogEnabled } from '../posthog';

type CheckoutType =
  | 'class'
  | 'class-pack'
  | 'subscription'
  | 'topup'
  | 'gift-card';

export type EligibilityBlockReason =
  | 'new_customers_only'
  | 'already_purchased'
  | 'active_subscription_exists'
  | 'not_found';
type ProductType =
  | 'class'
  | 'class_pack'
  | 'subscription'
  | 'topup'
  | 'gift_card';
type CouponType = 'STRIPE' | 'CUSTOM' | 'FREE';
type LoginMethod = 'otp_email' | 'otp_sms';
type VerifyMethod = 'otp' | 'password';
type DeliveryMethod = 'email' | 'sms';
type PlanChangeType = 'UPGRADE' | 'DOWNGRADE';
export type PlanSwitchSource = 'banner' | 'credits_card';
type OtpEventPurpose = 'login' | 'signup';
type SlotMode = 'book' | 'reschedule';
type ViewMode = 'daily' | 'monthly';
type CalendarType = 'google' | 'apple' | 'outlook' | 'ics';
type CreditsCodeType = 'class_pack' | 'subscription';
type AccountTab = 'profile' | 'password';

export type WarningSource = 'class_page' | 'checkout';

type EventMap = {
  scheduler_checkout_started: {
    checkout_type: CheckoutType;
    product_name: string;
    product_id: string;
    currency: string;
    total_amount: number;
    item_count: number;
    is_authenticated: boolean;
  };
  scheduler_checkout_item_removed: {
    checkout_type: CheckoutType;
    product_name: string;
    item_count_after: number;
  };
  scheduler_checkout_quantity_changed: {
    checkout_type: CheckoutType;
    product_name: string;
    old_quantity: number;
    new_quantity: number;
  };
  scheduler_coupon_applied: {
    checkout_type: CheckoutType;
    coupon_code: string;
    coupon_type: CouponType;
    discount_percent: number;
    discount_amount: number;
    original_amount: number;
    final_amount: number;
  };
  scheduler_coupon_failed: {
    checkout_type: CheckoutType;
    coupon_code: string;
    failure_reason: string;
    error_code?: string;
  };
  scheduler_eligibility_blocked: {
    checkout_type: CheckoutType;
    product_name: string;
    block_reason: EligibilityBlockReason;
    is_authenticated: boolean;
  };
  scheduler_rebook_warning_shown: {
    class_name: string;
    class_id: string;
    source: WarningSource;
    spots_booked: number;
  };
  scheduler_rebook_warning_action: {
    action: 'confirmed' | 'dismissed';
    source: WarningSource;
    class_name: string;
    class_id: string;
  };
  scheduler_booking_closed_shown: {
    class_name: string;
    class_id: string;
    source: WarningSource;
    reason: BookingBlockReason;
  };
  scheduler_booking_closed_action: {
    action: 'see_other_times' | 'dismissed';
    source: WarningSource;
    class_name: string;
    class_id: string;
    reason: BookingBlockReason;
  };
  scheduler_checkout_submitted: {
    checkout_type: CheckoutType;
    product_name: string;
    product_id: string;
    currency: string;
    total_amount: number;
    final_amount: number;
    item_count: number;
    coupon_applied: boolean;
    is_free_order: boolean;
    is_authenticated: boolean;
  };
  scheduler_order_completed: {
    checkout_type: CheckoutType;
    order_id: string;
    product_name: string;
    currency: string;
    original_amount: number;
    final_amount: number;
    coupon_applied: boolean;
    coupon_code: string | null;
    // 'gift_card' when a money card paid it all.
    payment_method: 'stripe' | 'free' | 'gift_card';
  };
  scheduler_order_verification_failed: {
    checkout_type: CheckoutType;
    order_id: string | null;
    session_id: string | null;
    reason?: 'timeout' | 'refunded' | 'failed';
  };
  scheduler_checkout_price_mismatch: {
    checkout_type: CheckoutType;
    product_id: string | null;
    currency: string;
    stale_total_amount: number;
  };

  scheduler_product_viewed: {
    product_type: ProductType;
    product_name: string;
    product_id: string;
    // Null for products without a public slug (a slug-less top-up).
    product_slug: string | null;
    price: number | null;
    currency: string | null;
  };
  scheduler_product_added_to_cart: {
    product_type: ProductType;
    product_name: string;
    product_id: string;
    price: number | null;
    currency: string | null;
    quantity: number;
  };
  scheduler_slot_selected: {
    class_name: string;
    class_id: string;
    instructor_name: string;
    slot_date: string;
    slot_time: string;
    available_spots: number | null;
    mode: SlotMode;
  };
  scheduler_calendar_navigated: {
    class_name: string;
    direction: 'forward' | 'backward';
    target_month: string;
  };
  scheduler_instructor_filtered: {
    class_name: string;
    selected_count: number;
    total_count: number;
  };

  scheduler_login_started: {
    method: LoginMethod;
  };
  scheduler_login_completed: {
    method: VerifyMethod;
    delivery_method: DeliveryMethod | null;
  };
  scheduler_login_failed: {
    method: VerifyMethod;
    failure_reason: string;
  };
  scheduler_signup_started: Record<string, never>;
  scheduler_signup_completed: Record<string, never>;
  scheduler_signup_failed: {
    failure_reason: string;
  };
  scheduler_password_reset_requested: Record<string, never>;
  scheduler_logout: Record<string, never>;
  scheduler_profile_updated: {
    fields_changed: string[];
  };
  scheduler_password_changed: Record<string, never>;

  scheduler_subscription_paused: {
    subscription_name: string;
    subscription_code_id: string;
    credits_remaining: number | null;
  };
  scheduler_subscription_canceled: {
    subscription_name: string;
    subscription_code_id: string;
    credits_remaining: number | null;
  };
  scheduler_subscription_resumed: {
    subscription_name: string;
    subscription_code_id: string;
  };
  scheduler_subscription_resumed_on_new_plan: {
    subscription_code_id: string;
    current_plan_name: string;
    new_plan_name: string;
    recharged: boolean;
    currency: string | null;
  };
  scheduler_subscription_resume_plan_change_failed: {
    subscription_code_id: string;
    new_plan_name: string;
    error_code: string | null;
  };
  scheduler_subscription_reactivated: {
    subscription_name: string;
    subscription_code_id: string;
  };
  scheduler_plan_change_previewed: {
    current_plan_name: string;
    new_plan_name: string;
    change_type: PlanChangeType;
    current_price: number;
    new_price: number;
    currency: string | null;
  };
  scheduler_plan_change_confirmed: {
    current_plan_name: string;
    new_plan_name: string;
    change_type: PlanChangeType;
    new_price: number;
    currency: string | null;
  };
  scheduler_plan_change_failed: {
    error_code: string | null;
    status: number | null;
    attempt: number;
    support_reference: string;
    retryable: boolean;
    change_type: PlanChangeType;
    subscription_code_id: string;
  };
  scheduler_plan_downgrade_canceled: {
    subscription_name: string;
    subscription_code_id: string;
  };
  scheduler_plan_switch_started: {
    subscription_name: string;
    subscription_code_id: string;
    source: PlanSwitchSource;
  };
  scheduler_plan_switch_blocked: {
    subscription_code_id: string;
    new_plan_name: string;
    reason: EligibilityBlockReason;
  };
  scheduler_plan_switch_confirmed: {
    subscription_code_id: string;
    current_plan_name: string;
    new_plan_name: string;
    current_price: number;
    new_price: number;
    currency: string | null;
    direction: SwitchDirection;
  };
  scheduler_payment_method_update_started: {
    subscription_name: string;
    subscription_code_id: string;
  };

  scheduler_class_rescheduled: {
    class_name: string;
    class_id: string;
    order_id: string;
    new_slot_date: string;
    new_slot_time: string;
  };
  scheduler_class_booking_canceled: {
    class_name: string;
    order_id: string;
  };
  scheduler_waitlist_joined: {
    class_id: string;
    mode: WaitlistMode;
    position: number | null;
  };
  scheduler_waitlist_left: {
    waitlist_entry_id: string;
    status: WaitlistEntryStatus;
  };
  scheduler_waitlist_offer_confirmed: {
    // The claim GET does not project the entry id, so a claim-link confirm has
    // no id to send.
    waitlist_entry_id: string | null;
    via: 'in_app' | 'claim_link';
  };
  scheduler_waitlist_offer_expired_viewed: {
    waitlist_entry_id: string | null;
  };

  scheduler_otp_resent: {
    purpose: OtpEventPurpose;
    delivery_method: DeliveryMethod;
  };
  scheduler_otp_delivery_method_switched: {
    purpose: OtpEventPurpose;
    from_method: DeliveryMethod;
    to_method: DeliveryMethod;
  };
  scheduler_otp_verification_failed: {
    purpose: OtpEventPurpose;
    failure_reason: string;
  };
  scheduler_otp_rate_limited: {
    purpose: OtpEventPurpose;
    limit_type: 'user' | 'ip' | 'lock';
  };

  scheduler_home_date_selected: {
    date: string;
    day_of_week: string;
    is_today: boolean;
  };
  scheduler_home_week_navigated: {
    direction: 'prev' | 'next';
    visible_days: number;
  };
  scheduler_home_today_clicked: Record<string, never>;
  scheduler_home_date_picker_opened: Record<string, never>;
  scheduler_home_date_picker_selected: {
    date: string;
  };
  scheduler_home_filter_changed: {
    filter_type: 'class' | 'instructor';
    selected_count: number;
    total_count: number;
  };
  scheduler_home_empty_state_cta_clicked: {
    target_date: string;
    days_from_selected: number;
  };
  scheduler_view_mode_changed: {
    view_type: ViewMode;
  };

  scheduler_checkout_abandoned: {
    checkout_type: CheckoutType;
    product_name: string;
    has_coupon_applied: boolean;
    step: 'checkout';
  };
  scheduler_eligibility_select_another_clicked: {
    checkout_type: CheckoutType;
    product_name: string;
    block_reason: EligibilityBlockReason;
  };

  scheduler_credits_card_expanded: {
    code_type: CreditsCodeType;
    product_name: string;
  };
  scheduler_subscription_cancel_intent: {
    subscription_name: string;
    subscription_code_id: string;
  };
  scheduler_subscription_cancel_abandoned: {
    subscription_name: string;
    subscription_code_id: string;
  };

  scheduler_calendar_download_clicked: {
    calendar_type: CalendarType;
  };
  scheduler_order_shared: {
    share_method: string;
  };
  scheduler_directions_clicked: Record<string, never>;

  scheduler_account_tab_changed: {
    tab: AccountTab;
  };
  scheduler_unsaved_changes_resolved: {
    resolution: 'saved' | 'discarded';
    tab: AccountTab;
  };

  scheduler_checkout_login_nudge_shown: {
    checkout_type: CheckoutType;
    coupon_type: CouponType;
  };
  scheduler_checkout_login_nudge_dismissed: {
    checkout_type: CheckoutType;
    coupon_type: CouponType;
  };
  scheduler_checkout_login_nudge_clicked: {
    checkout_type: CheckoutType;
    coupon_type: CouponType;
  };

  scheduler_install_card_shown: { platform: InstallPlatform };
  scheduler_install_card_dismissed: { platform: InstallPlatform };
  scheduler_install_help_opened: {
    platform: InstallPlatform;
    source: InstallSource;
    can_prompt: boolean;
  };
  scheduler_install_prompt_answered: {
    platform: InstallPlatform;
    outcome: 'accepted' | 'dismissed';
  };
  scheduler_app_installed: { platform: InstallPlatform };
  scheduler_topup_offered: {
    surface: string;
    source_type: 'PACK' | 'SUBSCRIPTION';
    price: number;
    currency: string;
  };
  scheduler_topup_clicked: {
    surface: string;
    source_type: 'PACK' | 'SUBSCRIPTION';
    quantity: number;
    price: number;
    currency: string | null;
  };

  scheduler_checkout_guest_toggled: {
    checked: boolean;
    passes_left: number;
  };
  scheduler_guest_added_at_checkout: {
    class_id: string;
    class_name: string;
    guest_count: number;
    source: CreditsCodeType;
    new_guests_only: boolean;
  };
  scheduler_checkout_guest_refused: {
    error_code: string;
    guest_count: number;
  };
  scheduler_guest_added_later: {
    class_id: string;
    class_name: string;
    guest_count: number;
    source: CreditsCodeType;
    new_guests_only: boolean;
  };
  scheduler_guest_add_refused: {
    error_code: string;
    guest_count: number;
  };
  scheduler_guest_removed: {
    class_id: string;
    class_name: string;
    pass_returned: boolean;
  };
};

export const AnalyticsEvent = {
  CHECKOUT_STARTED: 'scheduler_checkout_started',
  CHECKOUT_ITEM_REMOVED: 'scheduler_checkout_item_removed',
  CHECKOUT_QUANTITY_CHANGED: 'scheduler_checkout_quantity_changed',
  COUPON_APPLIED: 'scheduler_coupon_applied',
  COUPON_FAILED: 'scheduler_coupon_failed',
  ELIGIBILITY_BLOCKED: 'scheduler_eligibility_blocked',
  REBOOK_WARNING_SHOWN: 'scheduler_rebook_warning_shown',
  REBOOK_WARNING_ACTION: 'scheduler_rebook_warning_action',
  BOOKING_CLOSED_SHOWN: 'scheduler_booking_closed_shown',
  BOOKING_CLOSED_ACTION: 'scheduler_booking_closed_action',
  CHECKOUT_SUBMITTED: 'scheduler_checkout_submitted',
  ORDER_COMPLETED: 'scheduler_order_completed',
  ORDER_VERIFICATION_FAILED: 'scheduler_order_verification_failed',
  CHECKOUT_PRICE_MISMATCH: 'scheduler_checkout_price_mismatch',

  PRODUCT_VIEWED: 'scheduler_product_viewed',
  PRODUCT_ADDED_TO_CART: 'scheduler_product_added_to_cart',
  SLOT_SELECTED: 'scheduler_slot_selected',
  CALENDAR_NAVIGATED: 'scheduler_calendar_navigated',
  INSTRUCTOR_FILTERED: 'scheduler_instructor_filtered',

  LOGIN_STARTED: 'scheduler_login_started',
  LOGIN_COMPLETED: 'scheduler_login_completed',
  LOGIN_FAILED: 'scheduler_login_failed',
  SIGNUP_STARTED: 'scheduler_signup_started',
  SIGNUP_COMPLETED: 'scheduler_signup_completed',
  SIGNUP_FAILED: 'scheduler_signup_failed',
  PASSWORD_RESET_REQUESTED: 'scheduler_password_reset_requested',
  LOGOUT: 'scheduler_logout',
  PROFILE_UPDATED: 'scheduler_profile_updated',
  PASSWORD_CHANGED: 'scheduler_password_changed',

  SUBSCRIPTION_PAUSED: 'scheduler_subscription_paused',
  SUBSCRIPTION_CANCELED: 'scheduler_subscription_canceled',
  SUBSCRIPTION_RESUMED: 'scheduler_subscription_resumed',
  SUBSCRIPTION_RESUMED_ON_NEW_PLAN: 'scheduler_subscription_resumed_on_new_plan',
  SUBSCRIPTION_RESUME_PLAN_CHANGE_FAILED:
    'scheduler_subscription_resume_plan_change_failed',
  SUBSCRIPTION_REACTIVATED: 'scheduler_subscription_reactivated',
  PLAN_CHANGE_PREVIEWED: 'scheduler_plan_change_previewed',
  PLAN_CHANGE_CONFIRMED: 'scheduler_plan_change_confirmed',
  PLAN_CHANGE_FAILED: 'scheduler_plan_change_failed',
  PLAN_DOWNGRADE_CANCELED: 'scheduler_plan_downgrade_canceled',
  PLAN_SWITCH_STARTED: 'scheduler_plan_switch_started',
  PLAN_SWITCH_BLOCKED: 'scheduler_plan_switch_blocked',
  PLAN_SWITCH_CONFIRMED: 'scheduler_plan_switch_confirmed',
  PAYMENT_METHOD_UPDATE_STARTED: 'scheduler_payment_method_update_started',

  CLASS_RESCHEDULED: 'scheduler_class_rescheduled',
  CLASS_BOOKING_CANCELED: 'scheduler_class_booking_canceled',

  WAITLIST_JOINED: 'scheduler_waitlist_joined',
  WAITLIST_LEFT: 'scheduler_waitlist_left',
  WAITLIST_OFFER_CONFIRMED: 'scheduler_waitlist_offer_confirmed',
  WAITLIST_OFFER_EXPIRED_VIEWED: 'scheduler_waitlist_offer_expired_viewed',

  OTP_RESENT: 'scheduler_otp_resent',
  OTP_DELIVERY_METHOD_SWITCHED: 'scheduler_otp_delivery_method_switched',
  OTP_VERIFICATION_FAILED: 'scheduler_otp_verification_failed',
  OTP_RATE_LIMITED: 'scheduler_otp_rate_limited',

  HOME_DATE_SELECTED: 'scheduler_home_date_selected',
  HOME_WEEK_NAVIGATED: 'scheduler_home_week_navigated',
  HOME_TODAY_CLICKED: 'scheduler_home_today_clicked',
  HOME_DATE_PICKER_OPENED: 'scheduler_home_date_picker_opened',
  HOME_DATE_PICKER_SELECTED: 'scheduler_home_date_picker_selected',
  HOME_FILTER_CHANGED: 'scheduler_home_filter_changed',
  HOME_EMPTY_STATE_CTA_CLICKED: 'scheduler_home_empty_state_cta_clicked',
  VIEW_MODE_CHANGED: 'scheduler_view_mode_changed',

  CHECKOUT_ABANDONED: 'scheduler_checkout_abandoned',
  ELIGIBILITY_SELECT_ANOTHER_CLICKED:
    'scheduler_eligibility_select_another_clicked',

  CREDITS_CARD_EXPANDED: 'scheduler_credits_card_expanded',
  SUBSCRIPTION_CANCEL_INTENT: 'scheduler_subscription_cancel_intent',
  SUBSCRIPTION_CANCEL_ABANDONED: 'scheduler_subscription_cancel_abandoned',

  CALENDAR_DOWNLOAD_CLICKED: 'scheduler_calendar_download_clicked',
  ORDER_SHARED: 'scheduler_order_shared',
  DIRECTIONS_CLICKED: 'scheduler_directions_clicked',

  ACCOUNT_TAB_CHANGED: 'scheduler_account_tab_changed',
  UNSAVED_CHANGES_RESOLVED: 'scheduler_unsaved_changes_resolved',

  CHECKOUT_LOGIN_NUDGE_SHOWN: 'scheduler_checkout_login_nudge_shown',
  CHECKOUT_LOGIN_NUDGE_DISMISSED: 'scheduler_checkout_login_nudge_dismissed',
  CHECKOUT_LOGIN_NUDGE_CLICKED: 'scheduler_checkout_login_nudge_clicked',

  INSTALL_CARD_SHOWN: 'scheduler_install_card_shown',
  INSTALL_CARD_DISMISSED: 'scheduler_install_card_dismissed',
  INSTALL_HELP_OPENED: 'scheduler_install_help_opened',
  INSTALL_PROMPT_ANSWERED: 'scheduler_install_prompt_answered',
  APP_INSTALLED: 'scheduler_app_installed',

  TOPUP_OFFERED: 'scheduler_topup_offered',
  TOPUP_CLICKED: 'scheduler_topup_clicked',

  CHECKOUT_GUEST_TOGGLED: 'scheduler_checkout_guest_toggled',
  GUEST_ADDED_AT_CHECKOUT: 'scheduler_guest_added_at_checkout',
  CHECKOUT_GUEST_REFUSED: 'scheduler_checkout_guest_refused',
  GUEST_ADDED_LATER: 'scheduler_guest_added_later',
  GUEST_ADD_REFUSED: 'scheduler_guest_add_refused',
  GUEST_REMOVED: 'scheduler_guest_removed',
} as const satisfies Record<string, keyof EventMap>;

type TrackCall = {
  [K in keyof EventMap]: Record<string, never> extends EventMap[K]
    ? { event: K; properties?: EventMap[K] }
    : { event: K; properties: EventMap[K] };
}[keyof EventMap];

export function track({ event, properties }: TrackCall): void {
  if (!isPostHogEnabled()) return;
  posthog.capture(event, properties);
}
